import React, { useState } from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { Upload, Pulldown, Link } from 'jbc-front/components/icons';
import Button from 'jbc-front/components/Button';

import * as Urls from 'src/constants/EndpointUrls';
import { redirectTo } from 'src/utils/Http';
import {
  REDUCER_NAME,
  syncAttendanceWithJbc,
  syncAttendanceWithCustomAPI,
  getImportJobs,
  getClientSyncTasks,
  getCustomApiSyncJobs,
  getCustomApiSemiMonthlyAttendanceSyncJobs,
  fetchCustomApiSyncJobStatus,
  fetchCustomApiSemiMonthlyAttendanceSyncJobStatus,
  getClientCustomFeatures,
  syncAttendanceSemiMonthly
} from 'src/reducers/employees/employeeAttendances';
import {
  takeAttendancesQueriedUrl,
  takePayConfirmedStateFromSearchForm,
  takePaymentRangeFormSearchForm,
  FORM_NAME_ATTENDANCES_SEARCH
} from 'src/reducers/searchForm';
import { getSelectOptions } from 'src/reducers/selectOptions';
import { getExtras } from 'src/reducers/common/listReducer';
import Balloon from 'src/features/Employees/components/Balloon';
import { isFreePlan } from 'src/reducers/clients/currentClient';
import FreePlanRestriction from 'src/components/FreePlanRestriction';

import SyncWithJbcModal from './SyncWithJbcModal';
import styles from './ActionSection.scss';

const ActionSection = ({
  downloadTemplateCsvPageUrl,
  isPayConfirmed,
  syncWithJbc,
  syncWithCustomAPI,
  fetchCustomApiSyncJobStatus,
  syncWithCustomAPIForSemiMonthly,
  fetchSemiMonthlyJobStatus,
  extras = {},
  selectedPayrollRulesGroup = {},
  clientPayrollRulesGroups = {},
  importJobs = {},
  clientSyncTasks = {},
  customApiSyncJobs = {},
  customApiSemiMonthlyAttendanceSyncJobs = {},
  clientCustomFeatures = {},
  planType,
  clientInfo = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isBalloonOpen, setIsBalloonOpen] = useState(false);

  const { t } = useTranslation();

  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);
  const syncStart = () => setSyncing(true);
  const setBalloonOpen = (isBalloonOpen) => setIsBalloonOpen(isBalloonOpen);
  const callBackSyncDone = () => setSyncing(false);

  const { syncTaskSettings = {}, ...targetPeriod } = extras;
  const { attendanceStartDate, attendanceEndDate, paymentDate, endDate } = targetPeriod;
  const isInvalidAttendancePeriod = !attendanceStartDate || !attendanceEndDate;

  let targetPeriodText;
  if (paymentDate && endDate) {
    const paymentDateText = moment(paymentDate).format('YYYY/MM/DD') + ' Payment';
    const closingDateText = moment(endDate).format('MM/DD Cutoff');
    targetPeriodText = `${paymentDateText}(${closingDateText})`;
  }

  const dataSync = {
    ...targetPeriod,
    ...selectedPayrollRulesGroup
  };

  const isSynchronousable = !!(syncTaskSettings.jbcKintai || []).find(
    (item) => item.jbcCsvFormatName && item.jbcCsvFormatId
  );
  const hasInProgressImportJob = !_.isEmpty(importJobs.inProgress) || !_.isEmpty(importJobs.waiting);
  const hasInProgressCustomApiSyncJob = !_.isEmpty(customApiSyncJobs.inProgress) || !_.isEmpty(customApiSyncJobs.waiting);
  const hasInProgressCustomApiSemiMonthlyAttendanceSyncJob =
    !_.isEmpty(customApiSemiMonthlyAttendanceSyncJobs.inProgress) || !_.isEmpty(customApiSemiMonthlyAttendanceSyncJobs.waiting);
  const hasInProgressSyncJob = !_.isEmpty(clientSyncTasks.inProgress);
  const hasRunningAttendanceJob =
    hasInProgressImportJob || hasInProgressSyncJob || hasInProgressCustomApiSyncJob || hasInProgressCustomApiSemiMonthlyAttendanceSyncJob;
  const syncMenuDisabled = isPayConfirmed || isInvalidAttendancePeriod || syncing || hasRunningAttendanceJob;
  const importMenuDisabled = isPayConfirmed || hasRunningAttendanceJob;
  const currentPayrollRule = clientPayrollRulesGroups.find(payrollRule => payrollRule.value === selectedPayrollRulesGroup.payrollRulesGroups);
  const isSemiMonthly = currentPayrollRule.paymentCount === 'twice';
  const lastSyncWithApiServer = clientInfo.lastSyncWithApiServer;
  const renderDisabledLinkWithNote = noteText => (
    <div className={styles.rel}>
      <div className={styles.note}>{t(noteText)}</div>
      <div className={styles.noteShape} />
      <a disabled className={styles.disabled} role="presentation">
        <Pulldown size={8} />
        {t('features.EmployeeAttendances.components.ActionSection.Acquire_data_from_JOBCAN_Attendance_Management')}
      </a>
    </div>
  );

  const renderEnabledLink = () => (
    <a className={`u-mr10 ${syncMenuDisabled ? styles.disabled : null}`} disabled={syncMenuDisabled} onClick={openModal} role="presentation">
      <Pulldown size={8} />
      {t('features.EmployeeAttendances.components.ActionSection.Acquire_data_from_JOBCAN_Attendance_Management')}
    </a>
  );

  const noteTextSemiMonthly = 'features.EmployeeAttendances.components.ActionSection.Please_contact_our_support_desk';
  const noteTextNonSemiMonthly = 'features.EmployeeAttendances.components.ActionSection.Please_set_from_setting_service_linkage';
  const renderLink = () => {
    if (isSemiMonthly) {
      if (!isPayConfirmed && !lastSyncWithApiServer) {
        return renderDisabledLinkWithNote(noteTextSemiMonthly);
      }
      return renderEnabledLink();
    }

    if (!isPayConfirmed && !isSynchronousable) {
      return renderDisabledLinkWithNote(noteTextNonSemiMonthly);
    }
    return renderEnabledLink();
  };

  let disabledReason = hasRunningAttendanceJob
    ? t('features.EmployeeAttendances.components.ActionSection.Cannot_edit_because_attendance_information_is_being_updated')
    : null;
  if (isFreePlan(planType)) {
    disabledReason = <FreePlanRestriction />;
  }

  return (
    <div className={styles.actionSection}>
      <div className={styles.button}>
        <Button
          onClick={() => setBalloonOpen(!isBalloonOpen)}
          className="ignore-react-onclickoutside"
          icon={<Link size={16} />}
          widthAuto
          disabled={hasRunningAttendanceJob || isFreePlan(planType)}
          disabledReason={disabledReason}
        >
          {t('features.EmployeeAttendances.components.ActionSection.Link_with_JOBCAN_Attendance_Management')}
        </Button>
        {isBalloonOpen && (
          <Balloon hide={() => setBalloonOpen(false)}>
            <ul className={styles.links}>
              <li>
                <a onClick={() => redirectTo(Urls.SETTING_EMPLOYEES_SYNC_ATTENDANCE_SHOW)} role="presentation">
                  <Pulldown size={8} />
                  {t('features.EmployeeAttendances.components.ActionSection.Staffs_attendance_link_setting')}
                </a>
              </li>
              <li>{renderLink()}</li>

              {Object.keys(clientCustomFeatures).length > 0 && (
                <li>
                  <a
                    className={`u-mr10 ${syncMenuDisabled ? styles.disabled : null}`}
                    disabled={syncMenuDisabled}
                    onClick={() => {
                      syncStart();
                      syncWithCustomAPI(attendanceStartDate, attendanceEndDate, selectedPayrollRulesGroup, callBackSyncDone);
                      setTimeout(() => {
                        fetchCustomApiSyncJobStatus(true);
                      }, 2000);
                    }}
                    role="presentation"
                  >
                    <Pulldown size={8} />
                    {t('features.EmployeeAttendances.components.ActionSection.sync_with_custom_API')}
                  </a>
                </li>
              )}
            </ul>
          </Balloon>
        )}
      </div>

      <Button
        primary
        onClick={() => redirectTo(downloadTemplateCsvPageUrl)}
        icon={<Upload />}
        disabled={importMenuDisabled}
        disabledReason={
          hasRunningAttendanceJob
            ? t('features.EmployeeAttendances.components.ActionSection.Cannot_edit_because_attendance_information_is_being_updated')
            : null
        }
      >
        {t('features.EmployeeAttendances.components.ActionSection.Attendance_items_CSV_upload')}
      </Button>
      <SyncWithJbcModal
        isOpen={isOpen}
        targetPeriodText={targetPeriodText}
        handleHide={closeModal}
        isSemiMonthly={isSemiMonthly}
        handleSync={() => {
          syncStart();
          syncWithJbc(dataSync, callBackSyncDone);
          closeModal();
        }}
        handleSyncSemiMonthly={() => {
          syncStart();
          syncWithCustomAPIForSemiMonthly(dataSync, callBackSyncDone);
          setTimeout(() => {
            fetchSemiMonthlyJobStatus(true);
          }, 2000);
          closeModal();
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  downloadTemplateCsvPageUrl: takeAttendancesQueriedUrl(
    Urls.EMPLOYEE_ATTENDANCE_DOWNLOAD_CSV_PAGE,
    getFormValues(FORM_NAME_ATTENDANCES_SEARCH)(state)
  ),
  isPayConfirmed: takePayConfirmedStateFromSearchForm(state, FORM_NAME_ATTENDANCES_SEARCH),
  selectedPayrollRulesGroup: takePaymentRangeFormSearchForm(state, FORM_NAME_ATTENDANCES_SEARCH),
  clientPayrollRulesGroups: getSelectOptions(state, 'uniquePayrollRuleGroups'),
  extras: getExtras(REDUCER_NAME, state) || {},
  importJobs: getImportJobs(state) || {},
  clientSyncTasks: getClientSyncTasks(state) || {},
  customApiSyncJobs: getCustomApiSyncJobs(state) || {},
  customApiSemiMonthlyAttendanceSyncJobs: getCustomApiSemiMonthlyAttendanceSyncJobs(state) || {},
  clientCustomFeatures: getClientCustomFeatures(state) || {},
  planType: _.get(state, 'currentClient.item.data.planType'),
  clientInfo: _.get(state, 'employeeAttendances.list.extras.clientInfo')
});

const mapDispatchToProps = (dispatch) => ({
  syncWithJbc: (attendanceStartDate, attendanceEndDate, callBackSyncDone) =>
    dispatch(syncAttendanceWithJbc(attendanceStartDate, attendanceEndDate, callBackSyncDone)),
  syncWithCustomAPIForSemiMonthly: (data, callBackSyncDone) => dispatch(syncAttendanceSemiMonthly(data, callBackSyncDone)),
  syncWithCustomAPI: (attendanceStartDate, attendanceEndDate, payrollRuleGroups, callBackSyncDone) =>
    dispatch(syncAttendanceWithCustomAPI(attendanceStartDate, attendanceEndDate, payrollRuleGroups, callBackSyncDone)),
  fetchCustomApiSyncJobStatus: () => dispatch(fetchCustomApiSyncJobStatus(true)),
  fetchSemiMonthlyJobStatus: () => dispatch(fetchCustomApiSemiMonthlyAttendanceSyncJobStatus(true))
});

export default connect(mapStateToProps, mapDispatchToProps)(ActionSection);
